import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { FULLY_UNAUTHENTICATED_APIS } from '../constants/unauthenticated-apis';
import { PasswordService } from '../contracts/password-service.contract';
import { ApiResponse, PasswordChangeConfirmedResponse, PasswordResetConfirmedResponse } from '../models/ApiResponse';
import { RestApiService } from '../stubs/rest-api-service.stub';

/**
 * Provides methods to interact with a user's password.
 */
@Injectable()
export class ApiPasswordService extends RestApiService implements PasswordService {
  /**
   * Requests to reset the password for a user with the given email address.
   *
   * @throws UserNotFoundException
   *
   * @param email The email address of the user.
   */
  public async resetPassword(email: string, captchaSolution?: string): Promise<void> {
    try {
      await firstValueFrom(
        this.httpClient.post<ApiResponse>(`${this.restUrl}${FULLY_UNAUTHENTICATED_APIS.AUTH_FORGOT_PWD_URI}`, {
          email,
          captchaSolution
        })
      );
    } catch (error) {
      this.convertToException(error);
    }
  }

  /**
   * Sets a new password for a user, using a reset token that has been obtained
   * from the email that has been sent after requesting the password reset.
   *
   * @throws UserNotFoundException
   * @throws TokenExpiredException
   * @throws InvalidTokenException
   * @throws WeakPasswordException
   *
   * @param email The users email address.
   * @param newPassword The new password of that user.
   * @param resetToken The token from the email, sent after requesting the password reset.
   */
  public async confirmPasswordReset(email: string, newPassword: string, resetToken: string): Promise<void> {
    try {
      await firstValueFrom(
        this.httpClient.post<PasswordResetConfirmedResponse>(`${this.restUrl}${FULLY_UNAUTHENTICATED_APIS.AUTH_FORGOT_PWD_CONFIRM_URI}`, {
          email,
          password: newPassword,
          confirmationCode: resetToken
        })
      );
    } catch (error) {
      this.convertToException(error);
    }
  }

  /**
   * Requests to change the password for a user with the given accessToken.
   *
   * @throws AuthenticationInvalidException
   * @throws LimitExceededException
   *
   * @param oldPassword The old password of the user.
   * @param newPassword The new password of the user.
   * @param accessToken Access token obtained during registration.
   */
  public async changePassword(oldPassword: string, newPassword: string, accessToken: string): Promise<void> {
    try {
      await firstValueFrom(
        this.httpClient.post<PasswordChangeConfirmedResponse>(`${this.restUrl}/auth-sign-up/authenticate/passwordChange`, {
          accessToken,
          oldPassword,
          newPassword
        })
      );
    } catch (error) {
      this.convertToException(error);
    }
  }
}
